'use client'

import Link from 'next/link'
import { Button, Text } from '@/design-system'

interface ErrorScreenProps {
  /** Invoked by the "Try again" button. Wire to the boundary's `reset()`. */
  onTryAgain: () => void
  /** The thrown error's `.message`, surfaced in the collapsible details. */
  errorMessage?: string | null
  /** Optional Next.js error digest from server-thrown errors. */
  errorDigest?: string | null
}

const SUPPORT_EMAIL = 'support@snaptransfer.app'

function buildContactHref(errorMessage?: string | null, errorDigest?: string | null): string {
  const subject = encodeURIComponent('SnapTransfer error report')
  const lines: string[] = ['Hi SnapTransfer team,', '', 'I hit an error on the app:']
  if (errorMessage) lines.push('', `Message: ${errorMessage}`)
  if (errorDigest) lines.push(`Digest: ${errorDigest}`)
  const body = encodeURIComponent(lines.join('\n'))
  return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`
}

export function ErrorScreen({ onTryAgain, errorMessage, errorDigest }: ErrorScreenProps) {
  const contactHref = buildContactHref(errorMessage, errorDigest)

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        textAlign: 'center',
        padding: '0 24px',
        background: 'var(--paper)'
      }}>
      <Text
        variant="display"
        as="div"
        style={{
          fontWeight: 900,
          fontSize: 'clamp(96px, 16vw, 200px)',
          letterSpacing: '-0.04em',
          lineHeight: 0.9,
          background: 'var(--hot)',
          color: 'var(--paper)',
          border: 'var(--border-lg) solid var(--ink)',
          padding: '0 24px',
          transform: 'rotate(-2deg)',
          boxShadow: 'var(--shadow-lg)'
        }}>
        500
      </Text>
      <Text variant="overline" as="p" weight={700} opacity={0.75} style={{ margin: '36px 0 24px' }}>
        Something went wrong
      </Text>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center' }}>
        <Button variant="primary" arrow="right" onClick={onTryAgain}>
          Try again
        </Button>
        <Link href="/" style={{ textDecoration: 'none' }}>
          <Button variant="ghost">Go home</Button>
        </Link>
        <a href={contactHref} style={{ textDecoration: 'none' }}>
          <Button variant="ghost">Contact support</Button>
        </a>
      </div>
      {(errorMessage || errorDigest) && (
        <details
          style={{
            marginTop: 32,
            maxWidth: 640,
            width: '100%',
            textAlign: 'left',
            fontFamily: 'var(--font-mono, monospace)',
            fontSize: 12,
            opacity: 0.7
          }}>
          <summary style={{ cursor: 'pointer', padding: '8px 0' }}>Error details</summary>
          {errorMessage && <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{errorMessage}</pre>}
          {errorDigest && <p style={{ margin: '8px 0 0' }}>Digest: {errorDigest}</p>}
        </details>
      )}
    </div>
  )
}
